import { Typography } from '@modulor/react';
import clx from 'classnames';
import type { FlatStringObject, TFunction } from 'commonTypes';
import { Fragment, memo } from 'react';
import type { Limits, LostProfitInfo, MerchantUserFeeResponce } from 'store/models';
import { getMerchantUserFeeTranslation } from 'utils/fee';

import { normalizedStringValue } from '../../../utils/math/math';
import { formMethodSubTitleTranslationKeys } from '../../payUtils';
import styles from './MethodCellContent.module.scss';

export interface PaymentMethodSubtitleProps {
  t: TFunction;
  processingTime?: string;
  isIncludeBonus?: boolean;
  fee: MerchantUserFeeResponce | string;
  lostProfitInfo?: LostProfitInfo | null;
  limits: Limits;
  currency?: string;
  isDisabled?: boolean;
}

const MethodSubtitleComponent = (props: PaymentMethodSubtitleProps) => {
  const {
    t,
    processingTime = '',
    fee,
    lostProfitInfo,
    isIncludeBonus = false,
    limits,
    currency = '',
    isDisabled = false,
  } = props;

  const testId = formMethodSubTitleTranslationKeys({
    processingTime,
    fee,
    limits,
  });

  const separator = ' • ';
  const bonusKey = isIncludeBonus ? 'PH.BONUS_INCLUDED' : '';
  const bonusText = t(bonusKey);
  const processingTimeText = t(processingTime) || '';
  const feeText = getMerchantUserFeeTranslation(fee, t);
  const minLimit = limits?.min || 0;
  const limitText =
    limits?.displayLimits && minLimit > 0
      ? t('PH.MIN_LIMIT_INFO_TEXT', {
          amount: normalizedStringValue(`${minLimit}`),
          currency,
        })
      : '';

  const bonusItem = isIncludeBonus
    ? {
        key: 'bonus',
        content: (
          <span data-testid="bonus-label" className={styles['subtitle--bonus']}>
            {bonusText}
          </span>
        ),
      }
    : {
        key: 'bonus',
        content: null,
      };

  const lostProfitItem = lostProfitInfo
    ? {
        key: 'lost-profit',
        content: (
          <span
            data-testid="lost-profit-label"
            data-translation-key={lostProfitInfo.text || ''}
            className={styles['subtitle--special']}
          >
            {t(lostProfitInfo.text, lostProfitInfo.props as FlatStringObject)}
          </span>
        ),
      }
    : {
        key: 'lost-profit',
        content: null,
      };

  const items = [
    {
      key: 'limit',
      content: limitText,
    },
    lostProfitItem,
    {
      key: 'time',
      content: processingTimeText,
    },
    {
      key: 'fee',
      content: feeText,
    },
    bonusItem,
  ];

  const filtered = items.filter((item) => Boolean(item.content));

  const classes = clx(styles.subtitle, {
    [styles['subtitle--disabled']]: isDisabled,
  });

  return (
    <Typography
      dataTestId="method-content-subtitle"
      variant="caption-1-regular"
      className={classes}
      tag="span"
      data-translation-key={testId}
    >
      {filtered.map((item, index) => (
        <Fragment key={item.key}>
          {item.content}
          {index < filtered.length - 1 && separator}
        </Fragment>
      ))}
    </Typography>
  );
};

export const MethodSubtitle = memo(MethodSubtitleComponent);
